import { useState } from "react";
import type {
  Panel,
  PanelLayoutReport,
  PanelRow,
  PanelRowId,
} from "@electro-planner/domain";
import type { PanelMutations } from "../hooks/useDomainMutations.ts";
import { SYMBOL_REGISTRY } from "./symbols/symbolRegistry.tsx";

/**
 * Visualiserar centralens DIN-skenor med alla placerade komponenter som
 * boxar skalade efter sin modul-bredd (1 TE = 17,5 mm på riktig skena). En
 * "Ej placerade"-rad längst ner listar rader som inte har railNumber satt —
 * drag dem till en skena eller klicka på skena-chippen.
 *
 * Drag-och-släpp använder HTML5 DnD för enkelhet. Inga externa bibliotek.
 */
export function DinLayout({
  panel,
  layout,
  mutations,
}: {
  panel: Panel | undefined;
  layout: PanelLayoutReport;
  mutations: PanelMutations;
}) {
  const capacity = panel?.modulesPerRail ?? 12;
  const [draggingId, setDraggingId] = useState<PanelRowId | null>(null);

  const onDragStart = (id: PanelRowId) => {
    setDraggingId(id);
  };
  const onDragEnd = () => setDraggingId(null);

  const onDropOnRail = (railNumber: number | null) => {
    if (!draggingId) return;
    mutations.setRowRail(draggingId, railNumber);
    setDraggingId(null);
  };

  const patchRails = (railCount: number) => {
    mutations.patchPanelMeta({ railCount });
  };
  const patchModules = (modulesPerRail: number) => {
    mutations.patchPanelMeta({ modulesPerRail });
  };

  return (
    <div className="flex h-full w-full flex-col gap-3 overflow-auto bg-slate-50 p-4">
      <div className="flex flex-wrap items-center gap-4 rounded border border-slate-200 bg-white px-3 py-2">
        <span className="text-sm font-medium text-slate-700">Kapsling</span>
        <label className="flex items-center gap-1 text-xs text-slate-600">
          Antal skenor
          <input
            type="number"
            min={1}
            max={10}
            value={panel?.railCount ?? 2}
            onChange={(e) => patchRails(parseInt(e.target.value, 10) || 1)}
            className="w-16 rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        <label className="flex items-center gap-1 text-xs text-slate-600">
          Moduler per skena
          <input
            type="number"
            min={1}
            max={60}
            value={panel?.modulesPerRail ?? 12}
            onChange={(e) => patchModules(parseInt(e.target.value, 10) || 12)}
            className="w-16 rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        {layout.anyOverflow ? (
          <span className="font-medium text-rose-700">
            ⚠ Minst en skena är överfylld — öka moduler per skena eller flytta rader
          </span>
        ) : (
          <span className="text-xs text-slate-500">
            {layout.rails.reduce((s, r) => s + r.used, 0)} / {layout.rails.length * capacity} TE totalt
          </span>
        )}
      </div>

      {layout.rails.map((rail) => (
        <RailStrip
          key={rail.railNumber}
          railNumber={rail.railNumber}
          capacity={rail.capacity}
          used={rail.used}
          overflow={rail.overflow}
          slots={rail.slots.map(({ row, modules, startPosition }) => ({
            row,
            modules,
            startPosition,
          }))}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDrop={() => onDropOnRail(rail.railNumber)}
          isDragging={draggingId !== null}
        />
      ))}

      <UnplacedTray
        rows={layout.unplaced}
        railCount={layout.rails.length}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDrop={() => onDropOnRail(null)}
        onAssignRail={(rowId, rn) => mutations.setRowRail(rowId, rn)}
        isDragging={draggingId !== null}
      />
    </div>
  );
}

function RailStrip({
  railNumber,
  capacity,
  used,
  overflow,
  slots,
  onDragStart,
  onDragEnd,
  onDrop,
  isDragging,
}: {
  railNumber: number;
  capacity: number;
  used: number;
  overflow: boolean;
  slots: Array<{ row: PanelRow; modules: number; startPosition: number }>;
  onDragStart: (id: PanelRowId) => void;
  onDragEnd: () => void;
  onDrop: () => void;
  isDragging: boolean;
}) {
  // Vi målar alltid hela skenan som `capacity` moduler — om `used` > capacity
  // flyter de sista boxarna ut till höger (röd bakgrund markerar överfyllnad).
  const displayModules = Math.max(capacity, used);
  const moduleWidth = 100 / displayModules;

  return (
    <div
      className={`rounded border bg-white p-2 ${
        overflow ? "border-rose-400" : "border-slate-200"
      }`}
      onDragOver={(e) => {
        if (isDragging) e.preventDefault();
      }}
      onDrop={onDrop}
    >
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-semibold text-slate-700">
          Skena {railNumber}
        </span>
        <span className={overflow ? "text-rose-600" : "text-slate-500"}>
          {used} / {capacity} TE
        </span>
      </div>
      <div className="relative h-14 rounded border border-slate-300 bg-slate-50">
        {/* TE-rutnät */}
        <div className="absolute inset-0 flex">
          {Array.from({ length: capacity }, (_, i) => (
            <div
              key={i}
              className="flex-1 border-r border-dashed border-slate-300 last:border-r-0"
            />
          ))}
        </div>
        {overflow ? (
          <div
            className="absolute top-0 bottom-0 bg-rose-100"
            style={{
              left: `${(capacity / displayModules) * 100}%`,
              right: 0,
            }}
            title="Utanför skenans kapacitet"
          />
        ) : null}
        {slots.map(({ row, modules, startPosition }) => {
          const left = ((startPosition - 1) / displayModules) * 100;
          const width = (modules / displayModules) * 100;
          return (
            <RowBox
              key={row.id}
              row={row}
              left={left}
              width={width}
              moduleWidth={moduleWidth}
              modules={modules}
              onDragStart={() => onDragStart(row.id)}
              onDragEnd={onDragEnd}
            />
          );
        })}
      </div>
    </div>
  );
}

function RowBox({
  row,
  left,
  width,
  moduleWidth,
  modules,
  onDragStart,
  onDragEnd,
}: {
  row: PanelRow;
  left: number;
  width: number;
  moduleWidth: number;
  modules: number;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const cat = categorize(row);
  const fill = CATEGORY_COLOR[cat];
  const specLabel = [
    row.ampRating !== undefined ? `${row.ampRating}A` : "",
    row.poles !== undefined ? `${row.poles}P` : "",
    row.characteristic ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  void moduleWidth;

  const Icon = iconFor(row);

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`absolute top-1 bottom-1 flex cursor-grab select-none items-center justify-center gap-1 rounded border border-slate-400 px-1 text-center active:cursor-grabbing ${fill}`}
      style={{ left: `${left}%`, width: `${width}%` }}
      title={`${row.designation} · ${CATEGORY_LABEL[cat]} ${specLabel} · ${modules} TE${row.usage ? ` · ${row.usage}` : ""}`}
    >
      {Icon && width > 6 ? (
        <span className="shrink-0 text-slate-700">
          <Icon size={22} />
        </span>
      ) : null}
      <div className="flex flex-col leading-tight">
        <span className="text-xs font-semibold">{row.designation}</span>
        {width > 8 ? (
          <span className="text-[10px] text-slate-600">{specLabel || CATEGORY_LABEL[cat]}</span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Hittar matchande symbol-komponent för raden. Testar först komponentens
 * exakta kortkod, faller sedan tillbaka på prefix-match (DB16 → DB-ikon).
 */
function iconFor(row: PanelRow): SvgIcon | null {
  const raw = (row.component ?? "").toUpperCase();
  if (!raw) return null;
  const exact = SYMBOL_REGISTRY.find((s) => s.shortCode.toUpperCase() === raw);
  if (exact) return exact.icon;
  const prefix = SYMBOL_REGISTRY.find((s) => raw.startsWith(s.shortCode.toUpperCase()));
  return prefix ? prefix.icon : null;
}

type SvgIcon = (typeof SYMBOL_REGISTRY)[number]["icon"];

function UnplacedTray({
  rows,
  railCount,
  onDragStart,
  onDragEnd,
  onDrop,
  onAssignRail,
  isDragging,
}: {
  rows: ReadonlyArray<PanelRow>;
  railCount: number;
  onDragStart: (id: PanelRowId) => void;
  onDragEnd: () => void;
  onDrop: () => void;
  onAssignRail: (rowId: PanelRowId, railNumber: number) => void;
  isDragging: boolean;
}) {
  return (
    <div
      className="rounded border border-dashed border-slate-300 bg-white p-3"
      onDragOver={(e) => {
        if (isDragging) e.preventDefault();
      }}
      onDrop={onDrop}
    >
      <div className="mb-2 text-xs font-semibold text-slate-600">
        Ej placerade ({rows.length})
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400">
          Alla rader är placerade på en skena. Dra hit en box för att koppla bort den.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {rows.map((row) => {
            const cat = categorize(row);
            return (
              <div
                key={row.id}
                draggable
                onDragStart={() => onDragStart(row.id)}
                onDragEnd={onDragEnd}
                className={`flex cursor-grab items-center gap-1 rounded border border-slate-400 px-2 py-1 text-xs active:cursor-grabbing ${CATEGORY_COLOR[cat]}`}
              >
                <span className="font-semibold">{row.designation}</span>
                <span className="text-slate-600">{CATEGORY_LABEL[cat]}</span>
                <span className="flex gap-0.5">
                  {Array.from({ length: railCount }, (_, i) => i + 1).map((rn) => (
                    <button
                      key={rn}
                      type="button"
                      onClick={() => onAssignRail(row.id, rn)}
                      className="rounded bg-white px-1 text-[10px] text-slate-600 hover:bg-slate-100"
                      title={`Flytta till skena ${rn}`}
                    >
                      →{rn}
                    </button>
                  ))}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

type Category = "DB" | "JFB" | "JFA" | "EB" | "LB" | "OTHER";

const CATEGORY_LABEL: Record<Category, string> = {
  DB: "MCB",
  JFB: "RCD",
  JFA: "RCBO",
  EB: "MCCB",
  LB: "SW",
  OTHER: "—",
};

const CATEGORY_COLOR: Record<Category, string> = {
  DB: "bg-sky-100",
  JFB: "bg-amber-100",
  JFA: "bg-emerald-100",
  EB: "bg-rose-100",
  LB: "bg-slate-200",
  OTHER: "bg-slate-100",
};

function categorize(row: PanelRow): Category {
  const comp = (row.component ?? "").toUpperCase();
  if (comp.startsWith("JFA") || comp.startsWith("RCBO")) return "JFA";
  if (comp.startsWith("JFB") || comp.startsWith("RCD")) return "JFB";
  if (comp.startsWith("DB") || comp.startsWith("MCB")) return "DB";
  if (comp.startsWith("EB") || comp.startsWith("MCCB")) return "EB";
  if (comp.startsWith("LB") || comp.startsWith("LBS")) return "LB";
  return "OTHER";
}
